import { html, type TemplateResult } from "lit-html";
import type { AppState } from "../data/store";
import { store } from "../data/store";
import { applyAccountFilter, dollars, initials } from "../utils/format";
import { buildSchedule, formatTime, type Venue, type ScheduleEntry } from "../utils/schedule";

/**
 * Tonight's Schedule cube — meetups grouped by venue, in time order.
 *
 * Reads conversations.agreed_meet_at + agreed_meet_spot via the
 * already-loaded buyers slice; no new endpoint. Filters by the
 * dashboard-wide selectedAccountId so the account tabs (All / Yury /
 * Nikolay / Max) also affect the schedule.
 *
 * Clicking an entry opens the EditorSheet for that conversation so
 * Yury can edit time/spot inline.
 */
export function renderScheduleCube(state: AppState): TemplateResult {
  const buyers   = applyAccountFilter(state.buyers, state.selectedAccountId);
  const schedule = buildSchedule(buyers);
  const total    = schedule.Cosmo.length + schedule.Festival.length + schedule.Other.length;

  return html`
    <div class="cube cube--schedule">
      <div class="cube__head cube-shell__drag">
        <span class="cube__title">Tonight's schedule</span>
        <span class="cube__count">${total}</span>
      </div>
      <div class="cube__body">
        ${total === 0
          ? html`<div class="empty">
              No meets scheduled yet. Open any buyer in the Pipeline cube,
              set the meet spot to <strong>Cosmo</strong> or
              <strong>festival</strong>, and (Phase 5.B) pick a time.
            </div>`
          : html`
            ${renderVenue("Cosmo",    schedule.Cosmo)}
            ${renderVenue("Festival", schedule.Festival)}
            ${schedule.Other.length > 0 ? renderVenue("Other / unset venue", schedule.Other) : ""}
          `}
      </div>
    </div>
  `;
}

function renderVenue(label: string, entries: ScheduleEntry[]): TemplateResult {
  if (entries.length === 0) {
    return html`
      <section class="venue">
        <header class="venue__head">
          <h3 class="venue__name">${label}</h3>
          <span class="venue__count">0</span>
        </header>
        <div class="venue__empty">No meets here yet.</div>
      </section>
    `;
  }
  return html`
    <section class="venue">
      <header class="venue__head">
        <h3 class="venue__name">${label}</h3>
        <span class="venue__count">${entries.length}</span>
      </header>
      <ol class="venue__timeline">
        ${entries.map(renderEntry)}
      </ol>
    </section>
  `;
}

function renderEntry(e: ScheduleEntry): TemplateResult {
  const b = e.buyer;
  const isPaid     = b.conv_status === "paid";
  const isConflict = e.conflictsWith != null;
  const total  = b.conv_agreed_price_cents ?? null;
  const qty    = b.conv_tickets_count ?? null;
  return html`
    <li class="meet ${isPaid ? "meet--paid" : ""} ${isConflict ? "meet--conflict" : ""}"
        @click=${() => { if (b.conv_id) store.openEditor(b.conv_id); }}>
      <span class="meet__time">${formatTime(e.meetAt)}</span>
      <span class="meet__avatar ${isPaid ? "meet__avatar--paid" : ""}">${initials(b.display_name)}</span>
      <div class="meet__body">
        <div class="meet__name">
          ${b.display_name}
          ${isConflict ? html`<span class="meet__conflict-tag" title="Within 5 min of ${e.conflictsWith}">⚠ conflict</span>` : ""}
        </div>
        <div class="meet__sub">
          ${qty != null ? html`<span>${qty}×</span>` : ""}
          ${total != null ? html`<strong>${dollars(total)}</strong>` : ""}
          ${b.listing_title ? html`<span class="muted">· ${b.listing_title}</span>` : ""}
        </div>
      </div>
      ${b.conv_status ? html`<span class="pill pill--${b.conv_status}">${b.conv_status}</span>` : ""}
    </li>
  `;
}
